import React, { useState } from 'react';
import { Mail, Phone, MapPin, Star, Edit2, Trash2, MoreVertical, FileText, MessageSquare, Send } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ContactCard = ({ contact, onEdit, onDelete, onToggleFavorite, isHighlighted = false }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [menuRef, setMenuRef] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const navigate = useNavigate();

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef && !menuRef.contains(event.target)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu, menuRef]);

  const fullName = `${contact.first_name} ${contact.last_name}`;
  const initials = `${contact.first_name[0]}${contact.last_name[0]}`.toUpperCase();
  const fullAddress = [
    contact.address_line1,
    contact.address_line2,
    contact.city,
    contact.state_province,
    contact.postal_code,
    contact.country
  ].filter(Boolean).join(', ');

  return (
    <div 
      className={`bg-white border rounded-xl hover:shadow-lg transition-all duration-300 hover:scale-105 group relative ${
        isHighlighted 
          ? 'border-blue-500 border-2 bg-blue-50 shadow-lg ring-2 ring-blue-200' 
          : 'border-gray-200'
      }`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Avatar */}
            <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
              <span className="text-sm font-semibold text-blue-700">{initials}</span>
            </div>
            
            {/* Company & Name */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-gray-900 truncate">
                  {contact.company_name}
                </h3>
                {contact.is_favorite && (
                  <Star className="w-4 h-4 text-yellow-500 fill-yellow-500 flex-shrink-0" />
                )}
              </div>
              <p className="text-xs text-gray-600 truncate">{fullName}</p>
            </div>
          </div>

          {/* Actions Menu */}
          <div className="relative ml-2 flex-shrink-0" ref={setMenuRef}>
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
            >
              <MoreVertical className="w-4 h-4" />
            </button>

            {showMenu && (
              <div className="absolute right-0 top-8 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 py-1">
                <button
                  onClick={() => {
                    onEdit(contact);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Edit2 className="w-4 h-4" />
                  Edit Contact
                </button>
                <button
                  onClick={() => {
                    onToggleFavorite(contact);
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                >
                  <Star className={`w-4 h-4 ${contact.is_favorite ? 'fill-yellow-500 text-yellow-500' : ''}`} />
                  {contact.is_favorite ? 'Remove from Favorites' : 'Add to Favorites'}
                </button>
                <div className="border-t border-gray-100 my-1"></div>
                <button
                  onClick={() => {
                    if (window.confirm(`Are you sure you want to delete ${contact.company_name}?`)) {
                      onDelete(contact.id);
                    }
                    setShowMenu(false);
                  }}
                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Contact
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Contact Details */}
        <div className="space-y-2">
          {/* Email */}
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <Mail className="w-3.5 h-3.5 flex-shrink-0" />
            <a 
              href={`mailto:${contact.email}`}
              className="hover:text-blue-600 truncate"
            >
              {contact.email}
            </a>
          </div>

          {/* Phone */}
          {contact.phone && (
            <div className="flex items-center gap-2 text-xs text-gray-600">
              <Phone className="w-3.5 h-3.5 flex-shrink-0" />
              <a 
                href={`tel:${contact.phone}`}
                className="hover:text-blue-600"
              >
                {contact.phone}
              </a>
            </div>
          )}

          {/* Address */}
          <div className="flex items-start gap-2 text-xs text-gray-600">
            <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
            <span className="line-clamp-2">{fullAddress}</span>
          </div>
        </div>

        {/* Stats */}
        {(contact.invoice_count > 0 || contact.total_invoiced > 0) && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-1 text-gray-600">
                <FileText className="w-3.5 h-3.5" />
                <span>{contact.invoice_count} invoice{contact.invoice_count !== 1 ? 's' : ''}</span>
              </div>
              <div className="font-semibold text-gray-900">
                £{parseFloat(contact.total_invoiced || 0).toFixed(2)}
              </div>
            </div>
            {contact.last_invoice_date && (
              <div className="text-xs text-gray-500 mt-1">
                Last: {new Date(contact.last_invoice_date).toLocaleDateString()}
              </div>
            )}
          </div>
        )}

        {/* Notes Preview */}
        {contact.notes && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-600 line-clamp-2 italic">
              "{contact.notes}"
            </p>
          </div>
        )}

        {/* Quick Actions on Hover */}
        {isHovered && (
          <div className="mt-3 pt-3 border-t border-gray-100 animate-fadeIn">
            <button
              onClick={() => navigate(`/portal/send-invoice?contactId=${contact.id}`)}
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 bg-[#1F11ED] text-white text-xs font-medium rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Send className="w-3.5 h-3.5" />
              Send Invoice
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ContactCard;

